import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { FileText, Clock, CheckCircle, XCircle, DollarSign, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navigation from "../components/Navigation";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MySubmissionsPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pollingSession, setPollingSession] = useState(null);

  useEffect(() => {
    if (user) {
      fetchSubmissions();
      
      // Check if returning from payment
      const sessionId = searchParams.get('session_id');
      const paymentStatus = searchParams.get('payment');
      
      if (sessionId && paymentStatus === 'success') {
        pollPaymentStatus(sessionId);
      } else if (paymentStatus === 'cancelled') {
        toast.error('Payment was cancelled');
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [user, searchParams]);

  const fetchSubmissions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/client/my-submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Error loading submissions');
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh.');
      setPollingSession(null);
      return;
    }
    
    setPollingSession(sessionId);
    
    // Find submission with this session
    const submission = submissions.find(s => s.stripe_session_id === sessionId);
    if (!submission) {
      // Refresh and try again
      await fetchSubmissions();
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API}/client/submission/${submission.id}/payment-status?session_id=${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your submission is now pending approval.');
        setPollingSession(null);
        await fetchSubmissions();
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
      
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handlePayment = async (submissionId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/client/submission/${submissionId}/pay`, {
        submission_id: submissionId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error processing payment');
    }
  };

  const getStatusBadge = (submission) => {
    if (submission.approval_status === 'approved') {
      return (
        <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
          <CheckCircle className="w-3 h-3 mr-1" />
          Approved
        </span>
      );
    }
    if (submission.approval_status === 'rejected') {
      return (
        <span className="inline-flex items-center px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
          <XCircle className="w-3 h-3 mr-1" />
          Rejected
        </span>
      );
    }
    if (submission.approval_status === 'pending_payment') {
      return (
        <span className="inline-flex items-center px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded">
          <DollarSign className="w-3 h-3 mr-1" />
          Payment Required
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
        <Clock className="w-3 h-3 mr-1" />
        Pending Approval
      </span>
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <p className="text-slate-600">Please login to view your submissions.</p>
          <Button onClick={() => navigate('/login')} className="mt-4 bg-orange-500 hover:bg-orange-600">
            Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Fraunces, serif' }}>
              My Submissions
            </h1>
            <p className="text-slate-600 mt-1">Track the status of your document submissions</p>
          </div>
          <Button onClick={() => navigate('/upload')} className="bg-orange-500 hover:bg-orange-600">
            Submit New Document
          </Button>
        </div>

        {pollingSession && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6 flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
            <span className="text-blue-700">Verifying payment...</span>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
          </div>
        ) : submissions.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">No submissions yet</h3>
            <p className="text-slate-500 mb-4">You haven&apos;t submitted any documents yet.</p>
            <Button onClick={() => navigate('/upload')} className="bg-orange-500 hover:bg-orange-600">
              Submit Your First Document
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {submissions.map((submission) => (
              <div key={submission.id} className="bg-white border border-slate-200 rounded-md p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900">{submission.title}</h3>
                      {getStatusBadge(submission)}
                    </div>
                    
                    <div className="text-sm text-slate-500 space-y-1">
                      <p>Author: {submission.author_name}</p>
                      <p>Type: <span className="capitalize">{submission.document_type}</span> • {submission.word_count?.toLocaleString()} words</p>
                      <p>Submitted: {new Date(submission.created_at).toLocaleDateString()}</p>
                      {submission.is_owner && (
                        <p className="text-orange-600">Ownership: Verified (your name found in document)</p>
                      )}
                    </div>
                    
                    {submission.rejection_reason && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                        <strong>Rejection reason:</strong> {submission.rejection_reason}
                      </div>
                    )}
                    
                    {submission.item_id && (
                      <div className="mt-3">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/items/${submission.item_id}`)}
                        >
                          View Published Document
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {submission.approval_status === 'pending_payment' && (
                    <Button 
                      onClick={() => handlePayment(submission.id)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Pay ${submission.payment_amount?.toFixed(2)}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MySubmissionsPage;
